import React from 'react';
import { Box, Stack } from '@mui/material';
import { RPMText } from './subComponents/RPMText';

const DefaultPageRenderer = ({ pageData, colorTheme = 'white' }) => {
    const px = { xs: 2, md: 4 };
    const pt = { xs: 2, md: 4 };
    const pb = { xs: 2, md: 4 };

    // Extract page title from content
    const getPageTitle = () => {
        if (!pageData?.content?.rendered) return pageData?.title?.rendered || '';

        const parser = new DOMParser();
        const doc = parser.parseFromString(pageData.content.rendered, 'text/html');
        const h1 = doc.querySelector('h1');

        if (h1) {
            // Remove h1 from content since we'll render it separately
            h1.remove();
            return h1.textContent;
        }

        return pageData?.title?.rendered || '';
    };

    const title = getPageTitle();

    // Get content with title prepended as h1
    const getContentWithTitle = () => {
        if (!pageData?.content?.rendered && !title) return '';

        let content = pageData?.content?.rendered || '';

        // If we have a title and the content doesn't already start with an h1
        if (title && !content.startsWith('<h1')) {
            content = `<h1>${title}</h1>${content}`;
        }

        return content;
    };

    const contentWithTitle = getContentWithTitle();

    return (
        <Box
            className="slice default-page"
            px={px}
            pt={0}
            pb={pb}
            sx={{
                background: (theme) => theme.palette.background.default,
                minHeight: '100vh'
            }}
        >
            {/* Content Container with proper background layering */}
            <Box
                sx={{
                    maxWidth: { sm: "100%", lg: "1000px" },
                    mx: 'auto',
                    backgroundColor: "#F5F1EB", // Warm grey background
                    p: { xs: 3, md: 4 },
                    '& h1': {
                        fontSize: '3.6rem',
                        fontWeight: 500,
                        fontFamily: 'Kanit, sans-serif',
                        color: 'text.primary',
                        textAlign: 'center',
                        mt: 0,
                        mb: 4
                    },
                    '& h2': {
                        fontSize: '1.4rem',
                        fontWeight: 500,
                        fontFamily: 'Kanit, sans-serif',
                        color: 'text.primary',
                        mt: 4,
                        mb: 3
                    },
                    '& h3': {
                        fontSize: { xs: '1.25rem', md: '1.5rem' },
                        fontWeight: 500,
                        color: 'text.primary',
                        mt: 3,
                        mb: 2
                    },
                    '& h4, & h5, & h6': {
                        fontWeight: 500,
                        color: 'text.primary',
                        mt: 2,
                        mb: 2
                    },
                    '& p': {
                        color: 'text.secondary',
                        mb: 3,
                        lineHeight: 1.7,
                        fontSize: { xs: '1rem', md: '1.1rem' }
                    },
                    '& p strong, & strong': {
                        color: 'text.primary',
                        fontWeight: 600
                    },
                    '& a': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                        '&:hover': {
                            textDecoration: 'none'
                        }
                    },
                    '& ul, & ol': {
                        color: 'text.secondary',
                        mb: 3,
                        pl: 3,
                        lineHeight: 1.7,
                        '& li': {
                            mb: 1
                        }
                    },
                    '& blockquote': {
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        pl: 3,
                        ml: 0,
                        fontStyle: 'italic',
                        color: 'text.secondary',
                        backgroundColor: 'background.paper',
                        py: 2,
                        my: 3
                    }
                }}
                dangerouslySetInnerHTML={{ __html: contentWithTitle }}
            />
        </Box>
    );
};

export default DefaultPageRenderer;